import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Select,
  IconButton,
} from "@chakra-ui/react";
import { useAudioSnippetData } from "../hooks/useAudioSnippetData";
import { getAudioSnippetUrl } from "../services/audioSnippetService";
import { useUser } from "../contexts/UserContext";
import { Api } from "../Api";
import { formatDate } from "../utils/dateUtils";
import Pagination from "./shared/Pagination";
import { useToast } from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";

const AudioSnippetListTable = () => {
  const navigate = useNavigate();
  const { isAdmin } = useUser();
  const toast = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  
  // Use the audio snippet data hook with filter and pagination
  // Admins see all users (user_id: 0), normal users see only their own data (user_id: undefined)
  const { audioSnippets, loading, error, totalCount, refetch } = useAudioSnippetData({ 
    user_id: isAdmin ? 0 : undefined,
    skip: (currentPage - 1) * itemsPerPage,
    limit: itemsPerPage
  });
  const [currentAudio, setCurrentAudio] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioType, setAudioType] = useState("unfiltered");
  const [loadingAudio, setLoadingAudio] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const abortControllerRef = useRef(null);

  const handlePlayAudio = async (snippet) => {
    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setCurrentAudio(snippet);
    setLoadingAudio(true);
    onOpen();

    try {
      const url = await getAudioSnippetUrl(snippet.id, audioType, abortController.signal);
      if (url && !abortController.signal.aborted) {
        setAudioUrl(url);
      }
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error("Error loading audio URL:", err);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoadingAudio(false);
      }
    }
  };

  const handleAudioTypeChange = async (newType) => {
    setAudioType(newType);
    if (currentAudio) {
      // Abort any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setLoadingAudio(true);
      try {
        const url = await getAudioSnippetUrl(currentAudio.id, newType, abortController.signal);
        if (url && !abortController.signal.aborted) {
          setAudioUrl(url);
        }
      } catch (err) {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          console.error("Error loading audio URL:", err);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoadingAudio(false);
        }
      }
    }
  };

  const handleCloseModal = () => {
    // Abort any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setAudioUrl(null);
    setCurrentAudio(null);
    setLoadingAudio(false);
    onClose();
  };

  const handleDeleteSnippet = async (snippet) => {
    if (!window.confirm(`Are you sure you want to delete audio snippet ${snippet.id}? This action cannot be undone.`)) {
      return;
    }

    try {
      await Api.delete(`/audio_snippets/${snippet.id}`);
      toast({
        title: "Success",
        description: `Audio snippet ${snippet.id} deleted successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      refetch(); // Reload the list
    } catch (error) {
      console.error("Error deleting audio snippet:", error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete audio snippet",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatDuration = (startTimestamp, endTimestamp) => {
    if (!startTimestamp || !endTimestamp) return "N/A";
    const start = new Date(startTimestamp);
    const end = new Date(endTimestamp);
    const durationSeconds = (end - start) / 1000;
    
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = Math.floor(durationSeconds % 60);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={10}>
        <Text color="red.500">{error}</Text>
        <Button mt={4} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      <VStack spacing={6} align="stretch" p={6}>
        <Text fontSize="2xl" fontWeight="bold" textAlign="center">
          Audio Snippets
        </Text>

        {audioSnippets.length === 0 ? (
          <Box textAlign="center" py={10}>
            <Text color="gray.500">No audio snippets found</Text>
          </Box>
        ) : (
          <TableContainer m={"0 auto"}>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>Start Time</Th>
                  <Th>End Time</Th>
                  <Th>Duration</Th>
                  <Th>Created</Th>
                  <Th textAlign="center">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {audioSnippets.map((snippet) => (
                  <Tr 
                    key={snippet.id}
                    onClick={() => navigate(`/audio-snippets/${snippet.id}`)}
                    cursor="pointer"
                    _hover={{ bg: "blue.50" }}
                    transition="background-color 0.2s"
                  >
                    <Td>{snippet.id}</Td>
                    <Td>{formatDate(snippet.start_timestamp)}</Td>
                    <Td>{formatDate(snippet.end_timestamp)}</Td>
                    <Td>
                      {formatDuration(
                        snippet.start_timestamp,
                        snippet.end_timestamp
                      )}
                    </Td>
                    <Td>{formatDate(snippet.created_at)}</Td>
                    <Td onClick={(e) => e.stopPropagation()} textAlign="center">
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        aria-label="Delete audio snippet"
                        onClick={() => handleDeleteSnippet(snippet)}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination Controls */}
        <Pagination
          currentPage={currentPage}
          totalItems={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          isDisabled={loading}
        />
      </VStack>

      {/* Audio Player Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Audio Snippet #{currentAudio?.id}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  <strong>Start:</strong>{" "}
                  {currentAudio && formatDate(currentAudio.start_timestamp)}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Duration:</strong>{" "}
                  {currentAudio &&
                    formatDuration(
                      currentAudio.start_timestamp,
                      currentAudio.end_timestamp
                    )}
                </Text>
              </HStack>

              <Box>
                <Text fontSize="sm" mb={2}>
                  Audio Type:
                </Text>
                <Select
                  value={audioType}
                  onChange={(e) => handleAudioTypeChange(e.target.value)}
                >
                  <option value="unfiltered">Unfiltered</option>
                  <option value="filtered">Filtered</option>
                </Select>
              </Box>

              {loadingAudio ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100px"
                >
                  <Spinner />
                </Box>
              ) : audioUrl ? (
                <Box>
                  <audio
                    controls
                    style={{ width: "100%" }}
                    src={audioUrl}
                    key={audioUrl}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <Text color="gray.500">
                    No audio available for this snippet
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleCloseModal}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AudioSnippetListTable;

